
import HotelSearchCriteria, { GuestType } from "./states/HotelSearchCriteria";

export interface Branding {
    logoUrl: string,
    bannerImageUrl?: string,
    channel: string,
    agentId: number,
    appId?: string
}

export interface LangIcon {
    en: string,
    my: string
}

export interface InitialConfigurationData {
    locations: Location[]
    townships: TownshipLocation[]
    featuredLocations: FeaturedLocation[]
}

export interface CustomerSupportInfo {
    hotLineNumber: string
    messengerLink: string
}

export interface Money {
    currency: string;
    numericValue: number;
}

export interface MultiLangString {
    en: string
    my: string
}

export interface AuthResponse {
    accessToken:string,
    walletUserId:string|null
}

export enum LocationType {
    TOWN = 'TOWN', 
    ATTRACTION = 'ATTRACTION', 
    HOTEL = 'HOTEL', 
    TOWNSHIP = 'TOWNSHIP'
}

export interface Location {
    id: number
    name: MultiLangString
    type: LocationType
}

export interface TownLocation extends Location {}

export interface Attraction extends Location {    
    townName: MultiLangString
}

export interface TownshipLocation extends Location {    
    townName: MultiLangString
}

export interface HotelLocation extends Location {
    townName: MultiLangString
    townshipName: MultiLangString
}

export interface FeaturedLocation {
    id: number
    locationId: number
    name: MultiLangString
    type: LocationType
    imageUrl: string
}

export interface UserAccountResultSet {
    id: string
    name: string
    cellPhoneNumber: string
    emailAddress: string
    pointEarning: number
    totalPoint: number
}

export interface PointEarning {
    amount: number
    expiresAt: string
}

export interface EngagementSummary {
    id:string
    type: string
    baseValue: number
    pointEarning: PointEarning
    createdAt: string
}

export interface UserAccountDetails {
    id: string
    name: string
    cellPhoneNumber: string
    emailAddress: string
    pointEarning: number
    totalPoint: number
    engagements: EngagementSummary[]
}

export class LocationResultSet {
    towns: TownLocation[]
    townships: TownshipLocation[]
    attractions: Attraction[]
    hotels: HotelLocation[]
    featuredLocations: FeaturedLocation[]

    constructor(towns: TownLocation[], townships: TownshipLocation[], attractions: Attraction[], hotels: HotelLocation[], featuredLocations: FeaturedLocation[]) {
        this.towns = towns;
        this.townships = townships;
        this.attractions = attractions;
        this.hotels = hotels;
        this.featuredLocations = featuredLocations;
    }
}

export interface OptionKeyValues {
    key: "filter_by_price_range" | "filter_by_township" | "sort_by_price"
    items: Item[]
}

export interface Item {
    value: string
    label: MultiLangString
    isSelected: boolean
}

export interface FilterFieldGroup {
    townshipOption: OptionKeyValues
    priceRangeOption: OptionKeyValues
}

export interface SortingFieldGroup {
    priceSortingOption: OptionKeyValues
}

export interface Amenity {
    code: string
    label: MultiLangString
    defaultIcon: string
}

export interface Photo {
    url: string
    description: string | null
}

export interface GeoLocation {
    latitude: number
    longitude: number
    googleMapUrl: string|null
}

export interface Town {
    id: number
    name: MultiLangString
    description: MultiLangString
    imageUrl: string | null
    geoLocation: GeoLocation
}

export interface RateSummaryAmount {
    minimumAmount: number
    maximumAmount: number
    averageAmount: number
}

export interface RateSummary {
    standardRateSummary: RateSummaryAmount
    discountAppliedSummary: DiscountAppliedSummary
}

export interface DiscountAppliedSummary {
    promotionId: number
    promotionType: string
    description: MultiLangString
    discountPercentage: number,
    stayDateRange: DateRange,
    bookingDateRange: DateRange,
    discountAppliedOnMinimumAmount: number,
    discountAppliedOnMaximumAmount: number,
    discountAppliedOnAverageAmount: number
}

export interface DateRange {
    startDate: string,
    endDate: string
}

export interface Township {
    id: number
    name: MultiLangString
    type: LocationType
}

export interface Pagination {
    offset: number
    limit: number
    totalResultRows: number
}

export interface NearByPlace {
    name: MultiLangString
    type: string
    description: MultiLangString;
    geoLocation: Geolocation;
    distanceInMiles: string;
}

export interface LocationHint {
    name: MultiLangString
    type: LocationType
    distanceInKm: number
}

export interface HotelDetailsByAgent {
    agentName: MultiLangString,
    hotelDetails: HotelDetail[]
}

export interface HotelDetailByAgent {
    agentName: MultiLangString,
    hotelDetail: HotelDetail
}

export interface HotelPromotionResult {
    town: Town
    hotelPromotions: HotelPromotion[]
}

export interface HotelPromotion {
    id: number
    code: string
    name: MultiLangString
    featuredPhotoUrl: string
    town: Town
    township: Township
    address: MultiLangString
    locationHint: LocationHint
    rateSummary: RateSummary
}

export interface HotelDetail{
    id: number
    code: string
    name: MultiLangString
    phoneNumber: string
    hasAllotment: boolean
    description: MultiLangString
    maxChildAge: number
    amenities: Amenity[]
    featuredAmenities: Amenity[]
    logoUrl: string
    featuredPhotoUrl: string
    address: MultiLangString
    geoLocation: GeoLocation
    town: Town|null
    nearbyPlaces: NearByPlace[]
    photoUrls: string[]
    regulation: Regulation
    roomTypes: RoomType[]
    valueAddedPromotion: ValueAddedPromotion
    rateSummary: RateSummary
}

export interface Hotel {
    id: number
    name: MultiLangString
    locationHint: LocationHint
    code: string
    hasAllotment: boolean
    description: MultiLangString
    maxChildAge: number
    amenities: Amenity[]
    featuredAmenities: Amenity[]
    featuredPhotoUrl: string
    address: MultiLangString
    geoLocation: GeoLocation
    photoUrls: string[]
    isPromoAvailable: boolean,
    valueAddedPromotion: ValueAddedPromotion
}

export interface ValueAddedPromotion {
    shortMessage: MultiLangString
    fullMessage: MultiLangString
}

export interface HotelResult {
    hotel: Hotel    
    rateSummary: RateSummary
}

export interface HotelResultSet {
    filterFieldGroup: FilterFieldGroup
    sortingFieldGroup: SortingFieldGroup
    hotelResults: HotelResult[]
    pagination: Pagination
}

export interface SearchCriteriaAndHotelResultSet {
    searchCriteria: HotelSearchCriteria
    hotelResultSet: HotelResultSet
}

export interface HotelResultListHolder {
    hotelResults: HotelResult[];
    isLoading: boolean;
    totalSize: number;
}

export interface BookingSessionCreateParams {
    hotelId: number;
    checkInDate: string;
    checkOutDate: string;
    numberOfRooms: number;
    numberOfGuests: number;
    guestType: GuestType;
}

// this should include the entire room type as property
export interface RoomTypeSelection {
    id: string,
    roomType: RoomType,
    numberOfAdults: number,
    numberOfChildren: number,
    numberOfExtraBeds: number,
    valueAddedPromotion: ValueAddedPromotion;
    ratePerNights: {
        date:string, 
        sellRate:number,
        discountApplied: DiscountApplied|null;
    }[],
    subTotals: RoomSelectionSubTotalAmount
}

export interface RoomSelectionSubTotalAmount {
    sellRateRoomCharge: number,
    sellRateExtraBedsCharge: number,
    discountApplied: number,
    sellRate: number
}

export interface BookingSession {
    bookingSessionId: string;
    checkInDate: string;
    checkOutDate: string;
    guestType: GuestType;
    numberOfRooms: number;
    numberOfGuests: number;
    hotel: Hotel;
    roomSelections: RoomTypeSelection[];
    totals: TotalAmounts;
}

export interface Regulation {
    termsConditions: MultiLangString;
    generalPolicy: MultiLangString;
    cancellationPolicy: MultiLangString;
    childPolicy: MultiLangString;
    checkInCheckOutPolicy: {
        checkInTime : MultiLangString,
        checkOutTime : MultiLangString
    }
}

export interface Bed {
    type: string;
    quantity: number;
}

export interface RoomType {
    id: number;
    name: MultiLangString;
    description: MultiLangString;  
    beds: Bed[];  
    featuredPhotoUrl: string;
    photoUrls: string[];
    amenities: Amenity[];
    maxChildAge: number;
    numberOfExtraBedsAllowed: number;
    standardOccupancy: number;    
}

export interface RoomTypeResult {
    roomType: RoomType;
    bookableRoomQuantity: number;
    valueAddedPromotion: ValueAddedPromotion;
    bookableOptions: RoomTypeBookableOption[];
}

export interface NightlyRate {
    date: string;
    baseRoomRate: Money;
    extraBedRate: Money|null;
    discountApplied: DiscountApplied|null;
}

export interface DiscountApplied {
    promotionId: number,
    promotionType: string,
    amount: Money
}

export interface RoomTypeBookableOption {
    id: string;
    title: string;
    description: string;
    isPromoAvailable: boolean;
    nightlyRates: NightlyRate[];
}

export interface RatePlanExtraBedOption {
    total: number;
    optionToken: string;
    numberOfExtraBeds: number;
    maxAdults: number;
}

export class TemporaryRoomTypeSelection {
    roomType: RoomType;
    bookableOption: RoomTypeBookableOption;
    numberOfExtraBeds: number;
    numberOfAdults: number;
    numberOfNights: number;

    constructor(roomType: RoomType, bookableOption: RoomTypeBookableOption, 
        numberOfExtraBeds: number, numberOfAdults: number) {
        this.roomType = roomType;
        this.bookableOption = bookableOption;
        this.numberOfExtraBeds = numberOfExtraBeds;
        this.numberOfAdults = numberOfAdults;
        this.numberOfNights = bookableOption.nightlyRates.length;
    }

    getSubtotalAmount(): number {
        let subtotalAmountReducer = (aggregate: number, nightlyRate: NightlyRate) => {
            let totalExtraBedAmount = (nightlyRate.extraBedRate != null)? 
                this.numberOfExtraBeds * nightlyRate.extraBedRate.numericValue : 0;
            return aggregate + nightlyRate.baseRoomRate.numericValue + totalExtraBedAmount;
        }
        return this.bookableOption.nightlyRates.reduce(subtotalAmountReducer, 0);
    };

    getDiscountAmount(): number {
        let discountAmountReducer = (aggregate: number, nightlyRate: NightlyRate) => {
            let discount = (nightlyRate.discountApplied != null)? nightlyRate.discountApplied.amount.numericValue: 0;
            return aggregate + discount;
        }
        return this.bookableOption.nightlyRates.reduce(discountAmountReducer, 0);
    }
}

export enum GenderType {
    MALE = 'MALE', 
    FEMALE = 'FEMALE', 
    UNDISCLOSED = 'UNDISCLOSED'
}

export interface GuestInfo {
    name: string;
    emailAddress: string;
    cellPhoneNumber: string;
    countryCode: string;
    gender: GenderType;
}

export interface UserAccountCriteria {
    name: string;
    cellPhoneNumber: string;
    emailAddress: string;
    newPassword: string;
    confirmPassword: string;
}

export interface UserAccountRequest {
    cellPhoneNumber: string;
    otpId: string;
    otp: string;
}

export interface OtpResultSet {
    otpId: string;
}

export interface BookerInfo {
    name: string;
    emailAddress: string;
    cellPhoneNumber: string;
}

export interface TentativeBookingResult {
    isAllowedPromoCode: boolean;
    isAllowedPartialPayment: boolean;
    tentativeBooking: TentativeBooking;
    paymentOptions: PaymentOption[];
}

export interface TentativeBooking {
    tentativeBookingId: string; //TODO to be refactored
    hotelId: number;
    hotelName: MultiLangString;
    hotelAddress: MultiLangString;
    phoneNumber: string;
    maxChileAge: number;
    checkInDate: string;
    checkOutDate: string;
    guest: GuestInfo;
    specialRequest: string;
    numberOfRooms: number;
    numberOfGuests: number;
    roomTypeSelections: RoomTypeSelection[];
    totals: TotalAmounts;
}

export class BookingSummaryCard {
    hotelName: MultiLangString;
    hotelAddress: MultiLangString;
    maxChileAge: number;
    checkInDate: string;
    checkOutDate: string;
    guest: GuestInfo|null;
    specialRequest: string;
    numberOfRooms: number;
    numberOfGuests: number;
    roomTypeSelections: RoomTypeSelection[];
    guestType: string;
    totals: TotalAmounts;

    constructor(hotelName: MultiLangString, hotelAddress: MultiLangString, maxChileAge: number, checkInDate: string, checkOutDate: string,
        guest: GuestInfo|null, specialRequest: string, numberOfRooms: number, numberOfGuests: number, roomTypeSelections: RoomTypeSelection[],
        guestType: string, totals: TotalAmounts){
            this.hotelName = hotelName;
            this.hotelAddress = hotelAddress;
            this.maxChileAge = maxChileAge;
            this.checkInDate = checkInDate;
            this.checkOutDate = checkOutDate;
            this.guest = guest;
            this.specialRequest = specialRequest;
            this.numberOfRooms = numberOfRooms;
            this.numberOfGuests = numberOfGuests;
            this.roomTypeSelections = roomTypeSelections;
            this.guestType = guestType;
            this.totals = totals;
    }

    static mapToBookingSummaryCardFromBookingSession(bookingSession: BookingSession): BookingSummaryCard {
        return new BookingSummaryCard(bookingSession.hotel.name, bookingSession.hotel.address, bookingSession.hotel.maxChildAge,
            bookingSession.checkInDate, bookingSession.checkOutDate, null, "", bookingSession.numberOfRooms, bookingSession.numberOfGuests,
            bookingSession.roomSelections, bookingSession.guestType, bookingSession.totals);
    }

    static mapToBookingSummaryCardFromTentativeBooking(tentativeBooking: TentativeBooking): BookingSummaryCard {
        let guestType = (tentativeBooking.guest.countryCode == "MM")? "LOCAL": "FOREGINER";

        console.log(tentativeBooking)

        return new BookingSummaryCard(tentativeBooking.hotelName, tentativeBooking.hotelAddress, tentativeBooking.maxChileAge,
            tentativeBooking.checkInDate, tentativeBooking.checkOutDate, tentativeBooking.guest, tentativeBooking.specialRequest,
            tentativeBooking.numberOfRooms, tentativeBooking.numberOfGuests, tentativeBooking.roomTypeSelections, 
            guestType, tentativeBooking.totals);
    }
}

export interface TotalAmounts {
    roomCharge:number, 
    extraBedCharge: number,
    discountApplied: number,
    taxRate:number,
    convenienceFee: number,
    sellRate:number
}

export interface PaymentInitiateParm {
    prepayId: string;
    orderInfo: string;
    sign: string;
    tradeType: string;
}

export interface BookingDetail {
    bookingId: string,
    reference: string,
    status: string,
    createdAt: string,
    checkInDate: string,
    checkOutDate: string,
    numberOfRooms: number,
    numberOfGuests: number,
    numberOfAdults: number,
    numberOfChildren: number,
    specialRequest: string,
    isFullyPaid: boolean,
    hotel: HotelDetail,
    roomTypeSelections: RoomTypeSelection[],
    totals: TotalAmounts,
    booker: BookerInfo,
    guest: GuestInfo,
    payments: Payment[]
}

export interface Payment {
    methodCode: string,
    amount: number,
    transactionReference: string,
    memo: string
}

export class ErrorDTO {
    type: string;
    title: string;
    status: number;
    detail: string;

    constructor(type:string, title:string, status:number, detail:string) {
        this.type = type;
        this.title = title;
        this.status = status;
        this.detail = detail;
    }
}

export class PromotionErrorDTO {
    type: string;
    title: string;
    status: number;
    message: MultiLangString;

    constructor(type:string, title:string, status:number, message:MultiLangString) {
        this.type = type;
        this.title = title;
        this.status = status;
        this.message = message;
    }
}

export class SearchCriteriaTokenPayload {
    checkInDate: string;
    checkOutDate: string;
    numberOfRooms: number;
    guestType: GuestType;

    constructor(checkInDate:string, checkOutDate:string, numberOfRooms:number, guestType: GuestType) {
        this.checkInDate = checkInDate;
        this.checkOutDate = checkOutDate;
        this.numberOfRooms = numberOfRooms;
        this.guestType = guestType;
    }
}

export interface BookingLookupOTP {
    otp: string;
}

export class BookingSummary {
    bookingId: string;
    reference: string;
    checkInDate: string;
    checkOutDate: string;
    hotelName: string;
    hotelFeaturePhotoUrl: string;
    numberOfRoom: number;
    totalAmount: number;

    constructor(bookingId: string, reference: string, checkInDate: string, checkOutDate: string, hotelName: string, hotelFeaturePhotoUrl: string,
        numberOfRoom: number, totalAmount: number){ 
        this.bookingId = bookingId;
        this.reference = reference;
        this.checkInDate = checkInDate;
        this.checkOutDate = checkOutDate;
        this.hotelName = hotelName;
        this.hotelFeaturePhotoUrl = hotelFeaturePhotoUrl;
        this.numberOfRoom = numberOfRoom;
        this.totalAmount = totalAmount;
    }

    getNumberOfNight(): number{
        let differentInTime = new Date(this.checkOutDate).getTime() - new Date(this.checkInDate).getTime();
        return differentInTime / (1000 * 3600 * 24);
    }
}

export interface MoneyDto {
    currency: "MMK"|"USD",
    numericValue: number
}

export interface IntlStringDto {
    en:string;
    my:string;
}

export function getIntlStringValue(intString:IntlStringDto, lang:string) {
    return (lang == "en")? intString.en : intString.my;
}

export interface PaymentMethod {
    code: string,
    logoUrl: string,
    name: string, 
    description: string,
    instruction: IntlStringDto,
    terms: IntlStringDto
}

export interface AppliedPromoCodeInfo {
    promoCode: string,
    promoDetails: string,
    promoDiscount: MoneyDto
}

export interface PaymentOption {
    paymentMethod: PaymentMethod,
    roomCharges: MoneyDto,
    extraBedCharges: MoneyDto,
    tax: MoneyDto,
    fees: MoneyDto,
    discount: MoneyDto,
    promoCodeDiscount: MoneyDto|null,
    total: MoneyDto,    
    appliedPromoCodeInfo: AppliedPromoCodeInfo|null,
    paymentPatterns: PaymentPattern[]
}

export interface PaymentPattern {
    percentage: number,
    redirectUrl: string,
    token: string,
    isInstantConfirmationAllowed: boolean,
    responseTime: string
}

export interface PromoVerifyRequest {
    channel: string,
    hotelId:number,
    paymentMethodCode: string,
    promoCode: string,
    tentativeBookingId: string
}

type Left<E> = { tag: "left", error: E };
type Right<T> = { tag: "right", result: T };
export type Either<E, T> = Left<E> | Right<T>;

export interface PaymentIntentCreateRequest {
    paymentToken: string
}

export interface PaymentIntentCreateResponse {
    tentativeBookingId: string,
    paymentMethodCode: string,
    sseSubscriberUrl: string,
    totalAmount: MoneyDto,
    initiationParams: {
        merchOrderId: string,
        sign: string,
        orderInfo: string,
        prepayId: string
    }
}

export interface CheckConfirmation {
    isConfirmed: boolean,
    booking: BookingDetail,
    pointEarning: number
}

export interface PaymentConfirmation {
    willConfirmBooking: boolean,
    status: PaymentStatus,
    payments: Payment[],
    tentativeBooking: TentativeBooking,
}

export enum PaymentStatus {
    PROCESSING = 'PROCESSING', 
    SUCCESS = 'SUCCESS', 
    ERROR = 'ERROR'
}

export interface TermsAndConditions {
    termAndCondition: MultiLangString
}

export interface Article {
    title: string,
    body: MultiLangString
}

export enum Slug {
    TERMS = 'terms',
    FAQ = 'faq',
    PRIVACY = 'privacy'
}
